import React, { useEffect, useState } from "react";
import "./styles/dashboard.css";
import toast from "react-hot-toast";
import CommonHeader from "./components/CommonHeader";

const DriverManagement = () => {
  const [drivers, setDrivers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [formData, setFormData] = useState({
    driver_name: ""
  });
  const [loading, setLoading] = useState(false);

  const fetchDrivers = async () => {
    setLoading(true);
    try {
      const data = new URLSearchParams();
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/DriverLookup/getAllDrivers`, {
        method: "POST",
        headers: {
          Authorization: localStorage.getItem("token"),
        },
        body: data,
      });

      const result = await response.json();
      if (result.status === 1) {
        setDrivers(result.drivers || []);
      } else {
        toast.error(result.message || "Failed to fetch drivers");
      }
    } catch (error) {
      console.error("Error fetching drivers:", error);
      toast.error("Failed to fetch drivers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDrivers();
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.driver_name.trim()) {
      toast.error("Driver name is required");
      return;
    }

    setLoading(true);
    try {
      const endpoint = editingDriver 
        ? `${process.env.REACT_APP_API_URL}/DriverLookup/updateDriver`
        : `${process.env.REACT_APP_API_URL}/DriverLookup/createDriver`;

      const data = new URLSearchParams();
      if (editingDriver) {
        data.append('driver_id', editingDriver.driver_lookup_id);
      }
      data.append('driver_name', formData.driver_name);

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          Authorization: localStorage.getItem("token"),
        },
        body: data,
      });

      const result = await response.json();
      if (result.status === 1) {
        toast.success(result.message);
        setShowModal(false);
        setFormData({ driver_name: "" });
        setEditingDriver(null);
        fetchDrivers();
      } else {
        toast.error(result.message || "Operation failed");
      }
    } catch (error) {
      console.error("Error saving driver:", error);
      toast.error("Failed to save driver");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (driver) => {
    setEditingDriver(driver);
    setFormData({ driver_name: driver.driver_name });
    setShowModal(true);
  };

  const handleDelete = async (driverId, driverName) => {
    if (window.confirm(`Are you sure you want to delete driver "${driverName}"?`) === false) {
      return;
    }

    setLoading(true);
    try {
      const data = new URLSearchParams();
      data.append('driver_id', driverId);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/DriverLookup/deleteDriver`, {
        method: "POST",
        headers: {
          Authorization: localStorage.getItem("token"),
        },
        body: data,
      });

      const result = await response.json();
      if (result.status === 1) {
        toast.success(result.message);
        fetchDrivers();
      } else {
        toast.error(result.message || "Failed to delete driver");
      }
    } catch (error) {
      console.error("Error deleting driver:", error);
      toast.error("Failed to delete driver");
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (driverId, driverName) => {
    if (window.confirm(`Are you sure you want to restore driver "${driverName}"?`) === false) {
      return;
    }

    setLoading(true);
    try {
      const data = new URLSearchParams();
      data.append('driver_id', driverId);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/DriverLookup/restoreDriver`, {
        method: "POST",
        headers: {
          Authorization: localStorage.getItem("token"),
        },
        body: data,
      });

      const result = await response.json();
      if (result.status === 1) {
        toast.success(result.message);
        fetchDrivers();
      } else {
        toast.error(result.message || "Failed to restore driver");
      }
    } catch (error) {
      console.error("Error restoring driver:", error);
      toast.error("Failed to restore driver");
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingDriver(null);
    setFormData({ driver_name: "" });
  };

  return (
    <>
      <div class="d-flex flex-column flex-lg-row h-lg-full bg-surface-secondary">
        <div class="h-screen flex-grow-1 overflow-y-lg-auto">
          <CommonHeader />

          <main class="py-6 bg-surface-secondary">
            <div class="container-fluid">
              <div class="card shadow border-0 mb-7">
                <div class="card-header">
                  <h5 class="mb-0">Driver Management</h5>
                </div>
                <div class="table-responsive">
                  <table class="table table-hover table-nowrap">
                    <thead class="thead-light">
                      <tr>
                        <th scope="col">Driver ID</th>
                        <th scope="col">Driver Name</th>
                        <th scope="col">Status</th>
                        <th scope="col">Modified On</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colspan="5" class="text-center">
                            <div class="spinner-border" role="status">
                              <span class="visually-hidden">Loading...</span>
                            </div>
                          </td>
                        </tr>
                      ) : drivers.length > 0 ? (
                        drivers.map((driver) => (
                          <tr key={driver.driver_lookup_id}>
                            <td>{driver.driver_lookup_id}</td>
                            <td>{driver.driver_name}</td>
                            <td>
                              <span class="badge badge-lg badge-dot">
                                {driver.status == 0 ? (
                                  <>
                                    <i style={{ backgroundColor: "gray" }}></i>
                                    Inactive
                                  </>
                                ) : (
                                  <>
                                    <i className="bg-success"></i>
                                    Active
                                  </>
                                )}
                              </span>
                            </td>
                            <td>{new Date(driver.mod_on).toLocaleDateString()}</td>
                            <td class="text-end">
                              <button
                                onClick={() => handleEdit(driver)}
                                class="btn btn-sm btn-neutral"
                                style={{ marginRight: "0.5rem" }}
                              >
                                Edit
                              </button>
                              {driver.status == 1 ? (
                                <button
                                  type="button"
                                  class="btn btn-sm btn-square btn-neutral text-danger-hover"
                                  onClick={() => handleDelete(driver.driver_lookup_id, driver.driver_name)}
                                  title="Delete Driver"
                                >
                                  <i class="bi bi-trash"></i>
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  class="btn btn-sm btn-square btn-neutral text-success-hover"
                                  onClick={() => handleRestore(driver.driver_lookup_id, driver.driver_name)}
                                  title="Restore Driver"
                                >
                                  <i class="bi bi-arrow-clockwise"></i>
                                </button>
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colspan="5" class="text-center text-muted">No drivers found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div class="card-footer border-0 py-5">
                  <button
                    class="btn d-inline-flex btn-sm btn-primary mx-1"
                    onClick={() => setShowModal(true)}
                  >
                    <span class="pe-2">
                      <i class="bi bi-plus"></i>
                    </span>
                    <span>Add Driver</span>
                  </button>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* Add/Edit Driver Modal */}
      {showModal && (
        <div class="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div class="modal-dialog">
            <div class="modal-content">
              <div class="modal-header">
                <h5 class="modal-title">
                  {editingDriver ? 'Edit Driver' : 'Add New Driver'}
                </h5>
                <button 
                  type="button" 
                  class="btn-close" 
                  onClick={closeModal}
                ></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div class="modal-body">
                  <div class="mb-3">
                    <label for="driver_name" class="form-label">Driver Name</label>
                    <input
                      type="text"
                      class="form-control"
                      id="driver_name"
                      name="driver_name"
                      value={formData.driver_name}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter driver name"
                    />
                  </div>
                </div>
                <div class="modal-footer">
                  <button 
                    type="button" 
                    class="btn btn-secondary" 
                    onClick={closeModal}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    class="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span class="spinner-border spinner-border-sm me-2" role="status"></span>
                        Saving...
                      </>
                    ) : (
                      editingDriver ? 'Update Driver' : 'Add Driver'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DriverManagement;
